<template>
  <div
    class="toast__container"
    :class="`toast__container--${variant} ${extended && 'extended'}`"
    @click="dismiss"
  >
    <div
      class="toast__wrapper"
      :class="{ 'toast__wrapper--with-actions': actions.length > 0 }"
    >
      <slot />
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { ToastEvent, ToastAction } from './types'
import { useToast } from '@/uiKit/Toast/useToast'

const props = withDefaults(
  defineProps<{
    id: string
    extended?: boolean
    variant: ToastEvent
    actions?: ToastAction[]
  }>(),
  {
    extended: false,
    actions: () => [],
  },
)

const toast = useToast()

const dismiss = (): void => {
  toast.dismiss(props.id)
}
</script>

<style lang="scss" scoped>
@mixin assign-variant($borderColor, $background) {
  background: $background;
  border: 1px solid $borderColor;
}

.toast__container {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: fit-content;
  height: 42px;
  border-radius: 100px;
  cursor: pointer;

  transition: all 225ms linear;
  pointer-events: auto;

  &--default {
    @include assign-variant($colorStatusInformative, hsla(210, 80%, 98%, 1));
  }

  &--error {
    @include assign-variant($colorStatusNegative, hsla(0, 80%, 98%, 1));
  }

  &--warning {
    @include assign-variant($colorStatusWarning, hsla(30, 96%, 98%, 1));
  }

  &--success {
    @include assign-variant($colorStatusPositive, hsla(120, 52%, 98%, 1));
  }

  &--processing {
    @include assign-variant(var(--color-border-subtle), var(--color-surface-default));
    box-shadow: $effectShadowsLG;
  }
}

.extended {
  height: auto;
  border-radius: 12px;
}

.toast__wrapper {
  display: block;
  padding: 12px 16px 12px 12px;

  &--with-actions {
    padding-right: 4px;
  }
}
</style>
